import { z } from "zod";
import { optionalUuid } from "../lib/zod-helpers";
import { paginationSchema } from "./pagination";

/**
 * HCA Moravia — obchodnický checklist (Sprint 53, §C-04).
 * Per-produkt dotazník, run vázán na obchodní případ, score-based
 * doporučení (>=70 doporuceno, >=40 nerozhodnuto, <40 nedoporuceno).
 */

export const obchOtazkaTypSchema = z.enum(["bool", "scale_1_5", "text"]);
export type ObchOtazkaTyp = z.infer<typeof obchOtazkaTypSchema>;

const uuidMsg = "Neplatné UUID";
const nazevField = z
  .string()
  .min(1, "Název je povinný")
  .max(150, "Název musí být 150 znaků nebo méně");

export const createObchChecklistTemplateSchema = z.object({
  nazev: nazevField,
  productId: z.string().uuid(uuidMsg),
  popis: z.string().max(2000).optional(),
  aktivni: z.boolean().default(true),
});

export const updateObchChecklistTemplateSchema = z.object({
  id: z.string().uuid(uuidMsg),
  nazev: nazevField.optional(),
  popis: z.string().max(2000).optional(),
  aktivni: z.boolean().optional(),
});

export const obchChecklistIdSchema = z.object({ id: z.string().uuid(uuidMsg) });

export const addObchOtazkaSchema = z.object({
  templateId: z.string().uuid(uuidMsg),
  poradi: z.coerce.number().int().min(1).max(100),
  otazka: z.string().min(1).max(500),
  typ: obchOtazkaTypSchema,
  vaha: z.coerce.number().int().min(1).max(10).default(1),
});

export const updateObchOtazkaSchema = z.object({
  id: z.string().uuid(uuidMsg),
  poradi: z.coerce.number().int().min(1).max(100).optional(),
  otazka: z.string().min(1).max(500).optional(),
  typ: obchOtazkaTypSchema.optional(),
  vaha: z.coerce.number().int().min(1).max(10).optional(),
});

export const startObchRunSchema = z.object({
  opId: z.string().uuid(uuidMsg),
  templateId: z.string().uuid(uuidMsg),
});

export const submitObchOdpovedSchema = z.object({
  runId: z.string().uuid(uuidMsg),
  otazkaId: z.string().uuid(uuidMsg),
  odpovedBool: z.boolean().optional(),
  odpovedScale: z.coerce.number().int().min(1).max(5).optional(),
  odpovedText: z.string().max(5000).optional(),
});

export const completeObchRunSchema = z.object({ runId: z.string().uuid(uuidMsg) });

export const listObchRunsForOpSchema = z.object({
  opId: z.string().uuid(uuidMsg),
});

export const listObchTemplatesSchema = paginationSchema.extend({
  productId: optionalUuid(uuidMsg),
  aktivniOnly: z.coerce.boolean().optional(),
});

/** Doporučení podle skóre: >=70 doporuceno, >=40 nerozhodnuto, <40 nedoporuceno. */
export function computeObchDoporuceni(
  skorePct: number,
): "doporuceno" | "nerozhodnuto" | "nedoporuceno" {
  if (skorePct >= 70) return "doporuceno";
  if (skorePct >= 40) return "nerozhodnuto";
  return "nedoporuceno";
}

export type CreateObchChecklistTemplateInput = z.infer<typeof createObchChecklistTemplateSchema>;
export type UpdateObchChecklistTemplateInput = z.infer<typeof updateObchChecklistTemplateSchema>;
export type AddObchOtazkaInput = z.infer<typeof addObchOtazkaSchema>;
export type SubmitObchOdpovedInput = z.infer<typeof submitObchOdpovedSchema>;
export type StartObchRunInput = z.infer<typeof startObchRunSchema>;
